import {Component, inject, OnInit, ViewEncapsulation} from '@angular/core';
import moment from 'moment';
import {concatMap, flatMap, map, mergeMap, tap} from 'rxjs/operators';
import {BehaviorSubject, forkJoin, Observable, Subject} from 'rxjs';
import {ReviewService} from '../review.service';
import {HttpParams} from "@angular/common/http";
import {DisplayPipe} from '@shared/pipes/display.pipe';
import {Response} from '@shared/services/base.service';
import {FormsModule} from '@angular/forms';
import {NgbCollapseModule} from '@ng-bootstrap/ng-bootstrap';
import {NgForOf, NgIf} from '@angular/common';
import {OneStopService} from '../../one-stop/one-stop.service';
import {SearchComponent} from '@shared/search/search.component';
import {ActivatedRoute, Router} from '@angular/router';

declare var $: any;

@Component({
  selector: 'app-review-dashboard',
  templateUrl: './review-dashboard.component.html',
  styleUrls: ['./review-dashboard.component.css'],
  encapsulation: ViewEncapsulation.None,
  providers: [DisplayPipe],
  imports: [
    SearchComponent,
    FormsModule,
    NgbCollapseModule,
    NgIf,
    NgForOf
  ]
})
export class ReviewDashboardComponent implements OnInit {
  public endDate: string;
  public startDate: string;
  public dateRanges: { start: moment.Moment, end: moment.Moment }[];
  public dateIsCollapsed = true;
  public loadingLegend = true;
  public sortOrder: any;
  public loadingCompleteReviews = true;
  public loadingOngoingReviews = true;
  public loadingPermitsBySubmissionDate = true;
  public loadingOneStopByDateReceived = true;

  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);

  constructor(private reviewService: ReviewService, private displayPipe: DisplayPipe,
              private onesStopService: OneStopService) {
  }

  ngOnInit(): void {
    if (Object.keys(this.activatedRoute.snapshot.queryParams).length === 0) {
      this.router.navigate([], {
        queryParams: {
          start_date: moment().subtract(3, 'Q').startOf('quarter').format('YYYY-MM-DD'),
          end_date: moment().format('YYYY-MM-DD')
        },
        queryParamsHandling: 'merge',
      })
    }

    this.activatedRoute.queryParamMap.pipe(
      tap(params => {
        if (params.has('start_date')) {
          this.startDate = params.get('start_date');
        }
        if (params.has('end_date')) {
          this.endDate = params.get('end_date');
        }
      })
    ).subscribe(() => {
      this.updateRanges();
    });
  }

  updateQueryParams() {
    this.router.navigate([], {
      queryParams: {
        start_date: this.startDate,
        end_date: this.endDate
      },
      queryParamsHandling: 'merge',
    });
  }

  public updateRanges() {
    this.loadingLegend = false;
    this.setRanges();
    this.setSortOrder();
    this.getCompleteReviews().subscribe();
    this.getOngoingReviews().subscribe();
    this.getPermitsBySubmissionDate().subscribe();
    this.getOneStopPermitsByDateReceived().subscribe();
    // this.reviewService.getList<any>({page_size: 'max'}).subscribe((response) => {
    //   this.monitoringLocations = response;
    //   this.monitoring_locations_reduced = response.results.reduce((a, item) => {
    //     a[item.globalid] = item.name;
    //     return a;
    //   }, {});
    //   this.getSampleActivityCounts();
    //   this.getAdvisoryByStation();
    //   this.getResults();
    // });
    //
    //
    // this.loading_beach_advisories = true;
    // this.beachService.beachService.getList<any>({page_size: 'max'}).subscribe((response) => {
    //   this.beaches = response;
    //   this.beaches_reduced = response.results.reduce((a, item) => {
    //     a[item.globalid] = item.name;
    //     return a;
    //   }, {});
    //   this.getBeachAdvisories();
    // });
    //
    // this.getSubmissions();
  }

  public setRanges() {
    let currentQuarter = moment(this.endDate).quarter();
    const lastQuarter = this.getQuarterRange(currentQuarter);
    lastQuarter.end = moment(this.endDate);
    this.dateRanges = [lastQuarter];
    while (moment(this.startDate) < this.dateRanges[0].start) {
      currentQuarter--;
      this.dateRanges.unshift(this.getQuarterRange(currentQuarter));
    }
    this.dateRanges[0].start = moment(this.startDate);
  }


  private setSortOrder() {
    this.sortOrder = this.dateRanges.map((range) => {
      return this.getQuarterLabel(range.end);
    });
  }

  private convertObject(item, meta, dateStart) {
    const newItem: any = {};
    Object.keys(item).forEach((key) => {
      newItem[meta.fields[key].label] = this.displayPipe.transform(item[key], meta.fields[key].choices);
    });
    // if (item.hasOwnProperty('reviewer')) {
    //   newItem['Reviewer'] = item.monitoring_location];
    // }
    // else if (item.hasOwnProperty('beach')) {
    //   newItem.Beach = this.beaches_reduced[item.beach];
    // }
    newItem.Quarter = this.getQuarterLabel(dateStart);
    newItem['Month-Year'] = dateStart.format('MMM-YY');
    return newItem;
  }


  private getCompleteReviews() {
    this.loadingCompleteReviews = true;
    const observables: Observable<Response<any>>[] = [];

    this.dateRanges.forEach((dateRange, i) => {
      const params = new HttpParams()
        .set('last_edited_date__gt', dateRange.start.toISOString())
        .set('last_edited_date__lt', dateRange.end.toISOString())
        .set('page_size', '10000')
        .set('current_status', 'approved')
        .append('current_status', 'comments_complete')
        .append('current_status', 'rejected');
      observables.push(this.reviewService.getList<any>(params).pipe(map(response => {
        response.meta.dateRange = dateRange
        return response;
      })));
    });
    return forkJoin(observables).pipe(
      map(responses => responses.map(response => response.results.map((item) => {
        return this.convertObject(item, response.meta, moment(item.last_edited_date));
      }))),
      map(responses => responses.flat()),
      tap(results => {
        // get distinct permit ids
        const permitIDs = new Set(results.map(r => r.Record));

        this.renderPivot(results.filter(r => permitIDs.delete(r.Record)), '#permit_status', ['Type', 'Permit Status'], ['Quarter', 'Month-Year'])
        // this.renderPivot(results.filter(r => r['Is next'] && r.Status === 'Needs to Review' && r['Is group'] === false)
        // , '#current_reviewers_table', ['Reviewer'], ['Quarter'])
        // this.renderPivot(results.filter(r => r['Is group'] === false && r['Review Status'] === 'Needs to Review'), '#reviews_table', ['Reviewer'], ['Quarter'])
        this.renderPivot(results.filter(r => r['Is group'] === true), '#review_time_group', ['Reviewer'],
          ['Quarter', 'Month-Year'], null, null, null, null, 'Processing Time (Days)')
        // review_time_type
        this.renderPivot(results, '#review_time_type', ['Sub-Type', 'Permit Status'],
          ['Quarter', 'Month-Year'], null, null, null, null, 'Processing Time (Days)')
        this.renderPivot(results.filter(r => r['Is group'] === true), '#quarter_count_group', ['Reviewer'],
          ['Quarter', 'Month-Year'], null, null, null, null, null)
        this.renderPivotUI(results, '#completed_exploration', {'Month-Year': (r) => moment(r['Last Edited Date&Time']).format('MMM-YY')})
        this.loadingCompleteReviews = false;
      }));
  }

  private getOngoingReviews() {
    this.loadingOngoingReviews = true;
    const observables: Observable<Response<any>>[] = [];

    this.dateRanges.forEach((dateRange, i) => {
      const params = new HttpParams()
        .set('created_date__gt', dateRange.start.toISOString())
        .set('created_date__lt', dateRange.end.toISOString())
        .set('page_size', '10000')
        .set('current_status', 'needed')
        .append('current_status', 'in_review')
      observables.push(this.reviewService.getList<any>(params).pipe(map(response => {
        response.meta.dateRange = dateRange
        return response;
      })));
    });
    return forkJoin(observables).pipe(
      map(responses => responses.map(response => response.results.map((item) => {
        return this.convertObject(item, response.meta, moment(item.created_date));
      }))),
      map(responses => responses.flat()),
      tap(results => {
        // get distinct permit ids
        // const permitIDs = new Set(results.map(r => r.Record));

        // this.renderPivot(results.filter(r => permitIDs.delete(r.Record)), '#permit_status', ['Type'], ['Permit Status'])
        // this.renderPivot(results.filter(r => r['Is next'] && r.Status === 'Needs to Review' && r['Is group'] === false)
        // , '#current_reviewers_table', ['Reviewer'], ['Quarter'])
        this.renderPivot(results.filter(r => r['Is group'] === false), '#outstanding_individual_reviews_table', ['Reviewer'], ['Quarter', 'Month-Year'])
        this.renderPivot(results.filter(r => r['Is group'] === true), '#outstanding_group_reviews_table', ['Reviewer'], ['Quarter', 'Month-Year'])
        // this.renderPivot(results.filter(r => r['Is group'] === true && ['Approved', 'Comments Complete', 'Rejected'].includes(r['Permit Status'])), '#review_time_group', ['Reviewer'],
        //   ['Quarter'], null, null, null, null, 'Processing Time (Days)')
        // review_time_type
        // this.renderPivot(results, '#review_time_type', ['Sub-Type', 'Permit Status'],
        //   ['Quarter'], null, null, null, null, 'Processing Time (Days)')
        // this.renderPivot(results.filter(r => r['Is group'] === true && r['Permit Status'] === 'Approved'), '#quarter_count_group', ['Reviewer'],
        //   ['Quarter'], null, null, null, null, null)
        this.renderPivotUI(results, '#ongoing_exploration', {'Month-Year': (r) => moment(r['Created Date&Time']).format('MMM-YY')})
        this.loadingOngoingReviews = false;
      }));
  }

  getPermitsBySubmissionDate() {
    this.loadingPermitsBySubmissionDate = true;
    const observables: Observable<Response<any>>[] = [];

    this.dateRanges.forEach((dateRange, i) => {
      const params = new HttpParams()
        .set('submission_date__gt', dateRange.start.toISOString())
        .set('submission_date__lt', dateRange.end.toISOString())
        .set('page_size', '10000')
        .set('all_permits', 'true')
      observables.push(this.reviewService.getList<any>(params).pipe(map(response => {
        response.meta.dateRange = dateRange
        return response;
      })));
    });
    return forkJoin(observables).pipe(
      map(responses => responses.map(response => response.results.map((item) => {
        return this.convertObject(item, response.meta, moment(item.created_date));
      }))),
      map(responses => responses.flat()),
      tap(results => {
        // get distinct permit ids
        const permitIDs = new Set(results.map(r => r.Record));

        this.renderPivot(results.filter(r => permitIDs.delete(r.Record)), '#submitted_permits', ['Type', 'Sub-Type'], ['Quarter', 'Month-Year'])
        // this.renderPivot(results.filter(r => r['Is next'] && r.Status === 'Needs to Review' && r['Is group'] === false)
        // , '#current_reviewers_table', ['Reviewer'], ['Quarter'])
        // this.renderPivot(results.filter(r => r['Is group'] === false), '#outstanding_individual_reviews_table', ['Reviewer'], ['Quarter'])
        // this.renderPivot(results.filter(r => r['Is group'] === true), '#outstanding_group_reviews_table', ['Reviewer'], ['Quarter'])
        // this.renderPivot(results.filter(r => r['Is group'] === true && ['Approved', 'Comments Complete', 'Rejected'].includes(r['Permit Status'])), '#review_time_group', ['Reviewer'],
        //   ['Quarter'], null, null, null, null, 'Processing Time (Days)')
        // review_time_type
        // this.renderPivot(results, '#review_time_type', ['Sub-Type', 'Permit Status'],
        //   ['Quarter'], null, null, null, null, 'Processing Time (Days)')
        // this.renderPivot(results.filter(r => r['Is group'] === true && r['Permit Status'] === 'Approved'), '#quarter_count_group', ['Reviewer'],
        //   ['Quarter'], null, null, null, null, null)
        // this.renderPivotUI(results, '#ongoing_exploration')
        this.loadingPermitsBySubmissionDate = false;
      }));
  }

  private renderPivot(data, div, rows, cols, derivedAttributes ?, sumField ?, chartType ?, chartOptions ?, avgField ?) {
    $(() => {
      let renderer = $.pivotUtilities.renderers.Table;
      if (chartType === 'bar') {
        renderer = $.pivotUtilities.plotly_renderers['Bar Chart'];
      }
      const options: any = {
        derivedAttributes: derivedAttributes ? derivedAttributes : {},
        rows,
        cols,
        sorters: {
          Quarter: $.pivotUtilities.sortAs(this.sortOrder),
          'Month-Year': (a, b) => moment(a, 'MMM-YY').diff(moment(b, 'MMM-YY'))
        },
        renderer,
        rendererOptions: chartOptions
      };

      if (sumField) {
        const sum = $.pivotUtilities.aggregatorTemplates.sum;
        const numberFormat = $.pivotUtilities.numberFormat;
        const intFormat = numberFormat({digitsAfterDecimal: 0});
        options.aggregator = sum(intFormat)([sumField]);
      }
      if (avgField) {
        const avg = $.pivotUtilities.aggregatorTemplates.average;
        const numberFormat = $.pivotUtilities.numberFormat;
        const intFormat = numberFormat({digitsAfterDecimal: 1});
        options.aggregator = avg(intFormat)([avgField]);
      }

      $(div).pivot(data, options);
    });
  }

  private renderPivotUI(data, div, derivedAttributes ?) {
    const config = {
      ...this.getPivotUIFromLocalStorage(div),
      derivedAttributes: derivedAttributes ? derivedAttributes : {},
      renderers: $.extend(
        $.pivotUtilities.renderers,
        $.pivotUtilities.plotly_renderers
      ),
      sorters: {
        Quarter: $.pivotUtilities.sortAs(
          this.sortOrder
        ),
        'Month-Year': (a, b) => moment(a, 'MMM-YY').diff(moment(b, 'MMM-YY'))
      },
      onRefresh: (config) => this.setPivotUIToLocalStorage(div, config)
    };
    $(() => {
      $(div).pivotUI(data, config);
    });
  }

  public getQuarterLabel(date: moment.Moment) {
    if (date.quarter() === 4) {
      return 'Q1FY' + (parseInt(date.format('YY'), 10) + 1);
    } else {
      const tempQuarter = date.quarter() + 1;
      return 'Q' + tempQuarter + 'FY' + date.format('YY');
    }
  }

  private getQuarterRange(quarter: number) {
    const start = moment(this.endDate).quarter(quarter).startOf('quarter');
    const end = moment(this.endDate).quarter(quarter).endOf('quarter');
    return {start, end};
  }

  getOneStopPermitsByDateReceived() {
    this.loadingOneStopByDateReceived = true;
    const observables: Observable<Response<any>>[] = [];

    this.dateRanges.forEach((dateRange, i) => {
      const params = new HttpParams()
        .set('date_received_gte', dateRange.start.format('YYYY-MM-DD'))
        .set('date_received_lte', dateRange.end.format('YYYY-MM-DD'))
        .set('page_size', '10000')
      observables.push(this.onesStopService.permitsService.getList<any>(params).pipe(map(response => {
        response.meta.dateRange = dateRange
        return response;
      })));
    });
    return forkJoin(observables).pipe(
      map(responses => responses.map(response => response.results.map((item) => {
        return this.convertObject(item, response.meta, moment(item.created_date));
      }))),
      map(responses => responses.flat()),
      tap(results => {

        this.renderPivotUI(results, '#onestop_permits')
        // this.renderPivot(results.filter(r => r['Is next'] && r.Status === 'Needs to Review' && r['Is group'] === false)
        // , '#current_reviewers_table', ['Reviewer'], ['Quarter'])
        // this.renderPivot(results.filter(r => r['Is group'] === false), '#outstanding_individual_reviews_table', ['Reviewer'], ['Quarter'])
        // this.renderPivot(results.filter(r => r['Is group'] === true), '#outstanding_group_reviews_table', ['Reviewer'], ['Quarter'])
        // this.renderPivot(results.filter(r => r['Is group'] === true && ['Approved', 'Comments Complete', 'Rejected'].includes(r['Permit Status'])), '#review_time_group', ['Reviewer'],
        //   ['Quarter'], null, null, null, null, 'Processing Time (Days)')
        // review_time_type
        // this.renderPivot(results, '#review_time_type', ['Sub-Type', 'Permit Status'],
        //   ['Quarter'], null, null, null, null, 'Processing Time (Days)')
        // this.renderPivot(results.filter(r => r['Is group'] === true && r['Permit Status'] === 'Approved'), '#quarter_count_group', ['Reviewer'],
        //   ['Quarter'], null, null, null, null, null)
        // this.renderPivotUI(results, '#ongoing_exploration')
        this.loadingOneStopByDateReceived = false;
      }));
  }

  setPivotUIToLocalStorage(id: string, config: any) {
    const config_copy = {...config};
    delete config_copy["aggregators"];
    delete config_copy["renderers"];
    //delete some bulky default values
    delete config_copy["rendererOptions"];
    delete config_copy["localeStrings"];
    localStorage.setItem(id, JSON.stringify(config_copy));
  }

  getPivotUIFromLocalStorage(id: string): any | null {
    const config = localStorage.getItem(id);
    if (config) {
      return JSON.parse(config);
    }
    return null;
  }
}

